import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { EMPTY, Observable, TimeoutError, throwError } from 'rxjs';
import { catchError, timeout } from 'rxjs/operators';
import { CoreErrorCodes, HttpError, IApiOptions, InternalError } from 'src/app/core/contracts';
import { AppErrorHandler } from 'src/app/core/helpers/error.handler';
import { UI } from 'src/app/core/helpers/ui';
import { JSON_HEADER, formatDateProperties, isDev } from 'src/app/core/helpers/utils';
import { LOADING_MESSAGE, SHOW_LOADING } from 'src/app/core/interceptors/loading.interceptor';
import { environment } from 'src/environments/environment';

@Injectable()
export class Api {
  private http = inject(HttpClient);
  private errHandler = inject(AppErrorHandler);
  private ui = inject(UI);

  private readonly defaults: IApiOptions = {
    appendBaseUrl: true,
    handleError: true,
    handleLoading: true,
    headers: JSON_HEADER,
  };
  public readonly baseUrl = environment.url.replace(/\/?(\?|#|$)/, '/$1'); // append "/" if it's not already appended
  public get<T>(url: string, options?: IApiOptions) {
    return this.request<T>('GET', { url, options });
  }
  public post<T>(url: string, data?: unknown, options?: IApiOptions) {
    return this.request<T>('POST', { url, data, options });
  }
  public update<T>(url: string, data?: unknown, options?: IApiOptions) {
    return this.request<T>('PUT', { url, data, options });
  }
  public delete<T>(url: string, data?: unknown, options?: IApiOptions) {
    return this.request<T>('DELETE', { url, data, options });
  }
  private request<T>(method: string, { url, data = {}, options = {} }: { url: string; data?: unknown; options: IApiOptions }): Observable<T> {
    options = { ...this.defaults, ...options };
    if (!navigator.onLine) {
      this.errHandler.handleError(new InternalError('No internet connection', CoreErrorCodes.Offline, options.handleError));
      this.ui.hideLoading();
      return EMPTY;
    }
    url = options.appendBaseUrl ? this.appendBaseUrl(url) : url;
    options.body = data instanceof FormData ? data : formatDateProperties(structuredClone(data)); // clone data to avoid modifying the original object (which might have properties bound on the UI)
    options.url = url;
    if (options.handleLoading) {
      options.context = new HttpContext().set(LOADING_MESSAGE, options.loadingMessage || '');
      // no need to set SHOW_LOADING since it's default value is true
    } else {
      options.context = new HttpContext().set(SHOW_LOADING, false);
    }
    return this.http.request(method, url, options).pipe(
      timeout(isDev ? 1000 * 60 * 15 : 120000),
      catchError((response: HttpErrorResponse | TimeoutError | Error) => throwError(() => this.wrapError(response, options)))
    );
  }
  private wrapError(response: HttpErrorResponse | TimeoutError | Error, options: IApiOptions) {
    // debugger;
    if (response instanceof HttpErrorResponse || response instanceof TimeoutError) {
      return new HttpError(response.message, options, response);
    } else {
      // client side
      return new InternalError(response.message || response.toString(), CoreErrorCodes.Unknown, options.handleError, response);
    }
  }
  private appendBaseUrl(shortUrl: string) {
    // /legacy should be written inside the monthly data service as it's the only legacy api
    return this.baseUrl + 'api/' + shortUrl;
  }
}
